import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddressSuggestionsSelectComponent } from '@sdvor/delivery/ui/v2';
import { Pos } from '@shop/const';
import { ShopWorkingTimeService } from '@shop/feature';
import { GeocodeResult } from '@yandex-maps/const';
import { GeocodeService } from '@yandex-maps/feature';
import { BehaviorSubject, combineLatest, concat, filter, map, Observable, of, Subject } from 'rxjs';
import { NoShopsComponent } from '../no-shops';
import { ShopListComponent } from '../shop-list';
import { ShopMapComponent } from '../shop-map';

@Component({
  selector: 'sd-shop-desktop',
  imports: [CommonModule, NoShopsComponent, ShopListComponent, ShopMapComponent, AddressSuggestionsSelectComponent],
  templateUrl: './shop-desktop.component.html',
  styleUrls: ['./shop-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShopDesktopComponent implements OnInit, OnChanges {
  @Input() shops: Pos[];
  @Input() title: [string, string];

  mapInstance: ymaps.Map;
  shopsSorted$: Observable<Pos[]>;
  activeId$: BehaviorSubject<string>;
  activeShop$: Observable<Pos | undefined>;

  addressGeoObject$ = new Subject<GeocodeResult | undefined>();

  @ViewChild('shopListRef') shopListRef: ElementRef;

  protected shopsSubject$: BehaviorSubject<Pos[]> = new BehaviorSubject([] as Pos[]);

  constructor(
    private geocodeService: GeocodeService,
    private shopWorkingTimeService: ShopWorkingTimeService,
    protected destroyRef: DestroyRef,
    protected cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.activeId$ = new BehaviorSubject('');
    this.shopsSorted$ = this.getSortedShops();

    this.activeShop$ = combineLatest({ shopsSubject: this.shopsSubject$, activeId: this.activeId$ }).pipe(
      map(({ shopsSubject, activeId }) => shopsSubject?.find((shop) => shop.name === activeId)),
    );

    this.addressGeoObject$
      .pipe(
        filter((address): address is GeocodeResult => Boolean(address)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((address) => {
        const coords = address.geometry.getCoordinates();
        if (coords) {
          this.mapInstance.setCenter(coords);
          this.mapInstance.setZoom(13);
          this.mapInstance.geoObjects.each((geoObject) => {
            if (geoObject.options.get('id', {}) === 'userPlacemark') {
              this.mapInstance.geoObjects.remove(geoObject);
            }
          }, {});

          this.mapInstance.geoObjects.add(
            new ymaps.Placemark(
              coords,
              { iconCaption: address.getAddressLine().split(',').slice(1).join(',') || '' },
              {
                id: 'userPlacemark',
                preset: 'islands#orangeCircleDotIcon',
                hasBalloon: true,
                hideIconOnBalloonOpen: false,
                balloonCloseButton: false,
                iconCaptionMaxWidth: '200',
              },
            ),
          );
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shops?.currentValue) {
      this.shopsSubject$.next(this.shops);
    }
  }

  onChangeAddress(geocodeResult?: GeocodeResult): void {
    this.addressGeoObject$.next(geocodeResult);
  }

  onMapCreated(ymap: ymaps.Map): void {
    this.mapInstance = ymap;
  }

  selectShopMap(id: string, open: boolean = true): void {
    const openBalloon = (placemark: ymaps.Placemark): void => {
      if (id === placemark.properties?.get<string>('id', '')) {
        const coord = placemark.geometry.getCoordinates();
        if (coord) {
          this.mapInstance.panTo(coord).then(() => {
            if (open) {
              placemark.balloon.open();
            }
          });
        }
      }
    };

    this.mapInstance.geoObjects.each((geoObject: ymaps.Placemark) => {
      switch (typeof geoObject.getGeoObjects) {
        case 'function':
          geoObject.getGeoObjects().forEach((mark) => {
            openBalloon(mark as ymaps.Placemark);
          });
          break;
        default:
          openBalloon(geoObject);
      }
    }, {});
  }

  scrollToActiveShop(id: string): void {
    const shop = (this.shopListRef.nativeElement as HTMLDivElement).querySelector(`#shop-${id}`) as HTMLDivElement;

    if (shop) {
      this.shopListRef.nativeElement.scrollTo({
        top: shop.offsetTop - this.shopListRef.nativeElement.offsetTop,
        behavior: 'smooth',
      });
    }
  }

  onClickShop(id: string, zoom: number = 14): void {
    this.activeId$.next(id);
    this.mapInstance.setZoom(zoom);
    this.selectShopMap(id);
    this.scrollToActiveShop(id);
  }

  getPlacemarksProps = (shop: Pos): ymaps.IPlacemarkTemplate => {
    const workingTime = this.shopWorkingTimeService.getWorkingTimeInfo(shop).text;

    return this.getPlacemarksTemplate(shop, workingTime);
  };

  getPlacemarkIconOptions = (): Record<string, any> => ({
    balloonCloseButton: true,
  });

  getPlacemarksTemplate = (shop: Pos, workingTime: string): ymaps.IPlacemarkTemplate => {
    const goToShopBtn = (): string =>
      shop.url ? `<a href="${`https://sdvor.com${shop.url}`}" class="shop-link">о магазине</a>` : '';

    return {
      balloonContentHeader: `<div class="text-c1-l-500" style="margin-bottom: -6px;">${shop.addressFormatted}</div>`,
      balloonContentBody: `
        <div class="text-c1-l-300" style="display: flex; flex-direction: column; align-items: flex-start; gap: 8px;">
          <span>${shop.displayName}</span>
          <span>${workingTime}</span>
          ${goToShopBtn()}
        </div>
      `,
      clusterCaption: shop.addressFormatted,
      id: shop.name || '',
    };
  };

  protected getSortedShops(): Observable<Pos[]> {
    return combineLatest({
      shopsSubject: this.shopsSubject$,
      addressGeoObject: concat(of(undefined), this.addressGeoObject$),
    }).pipe(
      map(({ shopsSubject, addressGeoObject }) => {
        return addressGeoObject
          ? shopsSubject.map((shop: Pos) => {
              const latitude = shop.geoPoint?.latitude || 0;
              const longitude = shop.geoPoint?.longitude || 0;
              const addressCoords = addressGeoObject.geometry.getCoordinates() as ymaps.Coordinate;

              const shopCoords: ymaps.Coordinate = [latitude, longitude];

              return {
                ...shop,
                distanceKm: this.geocodeService.getDistanceInKm(shopCoords, addressCoords),
              };
            })
          : shopsSubject;
      }),
      map((shops) => {
        if (!shops.length) {
          return shops;
        }

        const firstShop: Pos = shops[0] as Pos;
        const hasDistanceKm = !!firstShop.distanceKm;

        const compareFn = hasDistanceKm
          ? (a: Pos, b: Pos): number => (a.distanceKm || 0) - (b.distanceKm || 0)
          : (a: Pos, b: Pos): number => (b.available || 0) - (a.available || 0);

        return shops.slice().sort(compareFn) as Pos[];
      }),
    );
  }
}
